import {MerchantUserJourney} from "../../../../client/state/merchant/MerchantUserJourneyStore.js";
import {isDeepEqual, isString, unwrapArray} from "../../../../blinkpay/UtilsLib.js";
import {Dispatchable} from "../../../../stem-core/base/Dispatcher";
import {wrapInSpinner} from "../../../../core/ui/LoadingSpinner.jsx";
import {apiEditMerchantSDKSettings} from "../../../../client/state/merchant/MerchantSDKSettingsStore.js";
import type {MerchantSDKSettings} from "../../../../client/state/merchant/MerchantSDKSettingsStore";


// How the settings' `journeys` option names a journey: by alias, or with A/B weights alongside it
type RawJourneyBranch = string | {alias: string; weight?: number; priority?: number};

// A journey that can be selected for an entrypoint. One per entrypoint, or multiple on A/B tests.
class JourneyEntrypointBranch {
    // settingsVersion is set in the constructor, the rest by the Object.assign of `options`
    declare settingsVersion: MerchantSDKSettings;
    declare journey: MerchantUserJourney;
    declare weight?: number;
    declare priority?: number;

    constructor(options: {journey: MerchantUserJourney; weight?: number; priority?: number}, settingsVersion: MerchantSDKSettings) {
        this.settingsVersion = settingsVersion;
        Object.assign(this, options);
    }

    // Ensure the journey exists in this settingsVersion
    static optionally(rawValue: RawJourneyBranch | null | undefined, settingsVersion: MerchantSDKSettings) {
        if (!rawValue) {
            return null;
        }
        if (isString(rawValue)) {
            rawValue = {alias: rawValue};
        }
        const journey = MerchantUserJourney.getByIdOrAlias(rawValue.alias, settingsVersion);
        return journey && new this({...rawValue, journey}, settingsVersion);
    }

    serialize() {
        let value: Record<string, any> = {
            alias: this.journey.alias || this.journey.id,
        }
        if (this.weight && this.weight !== 1) {
            value.weight = this.weight;
        }
        if (this.priority && this.priority !== 1) {
            value.priority = this.priority;
        }

        // Keeping this compact to minimize the SDK settings size
        if (Object.keys(value).length === 1) {
            return value.alias;
        }
        return value;
    }

    getJourney() {
        return this.journey;
    }
}


class UserJourneyEntrypoint {
    declare settingsVersion: MerchantSDKSettings;
    declare journeyBranches: JourneyEntrypointBranch[];

    constructor(rawJourneyEntrypoint: RawJourneyBranch | RawJourneyBranch[], settingsVersion: MerchantSDKSettings) {
        this.settingsVersion = settingsVersion;

        rawJourneyEntrypoint = unwrapArray(rawJourneyEntrypoint);
        this.journeyBranches = rawJourneyEntrypoint.map(value => JourneyEntrypointBranch.optionally(value, this.settingsVersion));
        this.journeyBranches = this.journeyBranches.filter(Boolean); // drop nulls
    }

    addJourney(journey: MerchantUserJourney) {
        const branch = JourneyEntrypointBranch.optionally(journey, this.settingsVersion);
        this.journeyBranches.push(branch);
    }

    serialize() {
        const value = this.journeyBranches.map(branch => branch.serialize());
        return (value.length === 1) ? value[0] : value;
    }

    getJourneys() {
        return this.journeyBranches.map(branch => branch.getJourney());
    }

    numBranches() {
        return this.journeyBranches.length;
    }

    getNormalizedWeights() {
        const weights = this.journeyBranches.map(branch => branch.weight || 1);
        const sum = weights.reduce((s, weight) => s + weight, 0);
        return weights.map(w => w / sum);
    }

    // Journeys that can be added to this entrypoint
    getAvailableJourneys() {
        const journeys = this.getJourneys();
        return MerchantUserJourney
            .filterBy({settingsVersion: this.settingsVersion})
            .filter(journey => journeys.indexOf(journey) === -1);
    }
}

// Keep an ordered list of JourneyEntrypoints
export class JourneyEntrypointState extends Dispatchable {
    // Assigned in the constructor and by load()
    declare settingsVersion: MerchantSDKSettings;
    declare entries: UserJourneyEntrypoint[];

    constructor(settingsVersion: MerchantSDKSettings) {
        super();
        this.settingsVersion = settingsVersion;
        this.load();
    }

    static isJourneyReferenced(settingsVersion: MerchantSDKSettings, userJourney: MerchantUserJourney) {
        const state = new JourneyEntrypointState(settingsVersion);

        for (const entrypoint of state.entries) {
            if (entrypoint.getJourneys().indexOf(userJourney) === -1) {
                return true;
            }
        }

        return false;
    }

    load() {
        const rawEntrypoints = this.settingsVersion.options?.journeys || [];
        this.entries = rawEntrypoints.map((entry: RawJourneyBranch | RawJourneyBranch[]) => new UserJourneyEntrypoint(entry, this.settingsVersion));
        this.dispatchChange();
    }

    serialize() {
        return this.entries.map(entry => entry.serialize());
    }

    isEqual(state: JourneyEntrypointState) {
        return isDeepEqual(this.serialize(), state.serialize());
    }

    // Editing operations
    dispatchChange() {
        this.dispatch("change");
    }

    removeEntry(index: number) {
        this.entries.splice(index, 1);
        this.dispatchChange();
    }

    // Append a single journey
    appendEntrypoint(userJourney: MerchantUserJourney) {
        this.entries.push(new UserJourneyEntrypoint([userJourney], this.settingsVersion));
        this.dispatchChange();
    }

    addJourneyToEntrypoint(index: number, userJourney: MerchantUserJourney) {
        this.entries[index].addJourney(userJourney);
        this.dispatchChange();
    }

    // A bit different than the add one
    removeJourneyFromEntrypoint(journeyEntrypoint: UserJourneyEntrypoint, journeyBranchIndex: number) {
        journeyEntrypoint.journeyBranches.splice(journeyBranchIndex, 1);
        this.dispatchChange();
    }

    swapEntries(indexA: number, indexB: number) {
        const temp = this.entries[indexA];
        this.entries[indexA] = this.entries[indexB];
        this.entries[indexB] = temp;
        this.dispatchChange();
    }

    moveUpEntry(index: number) {
        if (index > 0) {
            this.swapEntries(index - 1, index);
        }
    }

    moveDownEntry(index: number) {
        if (index + 1 < this.entries.length) {
            this.swapEntries(index, index + 1);
        }
    }

    // Same for all states
    isEdited() {
        const originalState = new JourneyEntrypointState(this.settingsVersion);
        return !this.isEqual(originalState);
    }

    discardChanges() {
        this.load();
    }

    @wrapInSpinner
    async saveChanges() {
        const newValue = this.serialize();
        const sdkSettings = this.settingsVersion;

        this.settingsVersion = await apiEditMerchantSDKSettings({
            sdkSettingsId: sdkSettings.id,
            options: {
                journeys: newValue,
            },
        });

        this.dispatchChange(); // No real change, only isEdited might be different now
    }
}
